import {
  leadsStatusesDestroyMutation,
  leadsTypesLeadsStatusesCreateMutation,
  leadsTypesLeadsStatusesListOptions,
  leadsTypesLeadsStatusesListQueryKey,
  leadsTypesLeadsStatusesPartialUpdateMutation,
  leadsTypesLeadsStatusesRetrieveOptions,
  PaginatedReturnLeadStatusListReadable,
  ReturnLeadStatusReadable
} from '@crm/api'
import { ConfirmModal, DndTable } from '@crm/components'
import {
  CatalogActions,
  CatalogAddButton,
  FormNomenclaturesLeadTypesStatuses
} from '@crm/features/nomenclatures/components'
import { useCapabilities } from '@crm/hooks/useCapabilities'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TitleSection } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Form, MenuProps, Modal, TableColumnsType } from 'antd'
import { Edit, Trash } from 'iconsax-react'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

export const NomenclaturesLeadTypesStatuses = () => {
  const { canManageCatalogs } = useCapabilities()

  const { id } = useParams()
  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()
  const { resetPagination } = useResetPagination()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()
  const [form] = Form.useForm()

  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [isCreate, setIsCreate] = useState(false)

  const [sorted, setSorted] = useState(false)
  const [data, setData] = useState<PaginatedReturnLeadStatusListReadable>()

  const { data: leadsTypesStatuses, isLoading } = useQuery({
    ...leadsTypesLeadsStatusesListOptions({
      query: {
        ...defaultFilters,
        ...params
      },
      path: {
        lead_type_id: String(id)
      }
    }),
    enabled: !!id
  })

  const { data: leadType, isLoading: isLoadingLeadType } = useQuery({
    ...leadsTypesLeadsStatusesRetrieveOptions({
      path: { id: Number(editItem), lead_type_id: String(id) }
    }),
    enabled: !!editItem
  })

  useEffect(() => {
    if (!leadsTypesStatuses) return
    setData(leadsTypesStatuses)
  }, [leadsTypesStatuses])

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        icon: <Edit size={20} />,
        key: 'edit',
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        icon: <Trash size={20} />,
        danger: true,
        key: 'delete',
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<ReturnLeadStatusReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('nomenclature.code'),
        dataIndex: 'code',
        key: 'code',
        sorter: true
      },
      {
        title: t('nomenclature.order'),
        dataIndex: 'order',
        key: 'order',
        sorter: true
      },
      {
        title: t('nomenclature.is_default'),
        key: 'is_default',
        sorter: true,
        render: ({ is_default }) =>
          is_default ? t('buttons.yes') : t('buttons.no')
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <CatalogActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  const updateMutation = useMutation({
    ...leadsTypesLeadsStatusesPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        leadsTypesLeadsStatusesListQueryKey({
          path: { lead_type_id: String(id) }
        }),
        onCloseModal()
      ])
    }
  })

  const createMutation = useMutation({
    ...leadsTypesLeadsStatusesCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        leadsTypesLeadsStatusesListQueryKey({
          path: { lead_type_id: String(id) }
        }),
        notification.success({
          message: t('success.success'),
          description: t('success.success_data_save')
        }),
        onCloseModal()
      ])
    }
  })

  useEffect(() => {
    if (data?.results && sorted) {
      setData(
        (prevState) =>
          ({
            ...prevState,
            results: data?.results?.map((params, i) => {
              if (
                i + 1 !==
                leadsTypesStatuses?.results?.find(
                  (item) => item.id === params.id
                )?.order
              ) {
                updateMutation.mutate({
                  path: { id: params.id, lead_type_id: String(id) },
                  body: { ...params, order: i + 1 }
                })

                return { ...params, order: i + 1 }
              }

              return params
            })
          } as PaginatedReturnLeadStatusListReadable)
      )
      setSorted(false)
    }
  }, [data, leadsTypesStatuses?.results, sorted, updateMutation])

  const deleteLeadTypeMutation = useMutation({
    ...leadsStatusesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        leadsTypesLeadsStatusesListQueryKey({
          path: { lead_type_id: String(id) }
        })
      ])
      resetPagination()
      onCloseModal()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onCloseModal = () => {
    setDeleteItem(undefined)
    setEditItem(undefined)
    setIsCreate(false)
    form.resetFields()
  }

  const onDelete = () =>
    deleteItem &&
    deleteLeadTypeMutation.mutate({
      path: { id: deleteItem }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (editItem) {
      updateMutation.mutate({
        path: { id: editItem, lead_type_id: String(id) },
        body: values
      })
    } else {
      createMutation.mutate({
        path: { lead_type_id: String(id) },
        body: values
      })
    }
  }

  const dndItems = data?.results?.map(({ id }) => id) || []

  const onSortEnd = (
    action: (state: ReturnLeadStatusReadable[]) => ReturnLeadStatusReadable[]
  ) => {
    setData((prev) => {
      const results = action(prev?.results || [])

      return {
        ...prev,
        results: results || prev?.results
      } as PaginatedReturnLeadStatusListReadable
    })

    setSorted(true)
  }

  useEffect(() => {
    if (leadType) {
      form.setFieldsValue(leadType)
    }
  }, [leadType, form])

  return (
    <>
      <TitleSection title={t(`nomenclature.lead_statuses`)} />

      <DndTable
        disabled={!canManageCatalogs}
        onDragEnd={onSortEnd}
        rightChildren={
          <CatalogAddButton onClick={() => setIsCreate(true)}>
            {t('nomenclature.add_status')}
          </CatalogAddButton>
        }
        items={dndItems}
        rowKey="id"
        data={data}
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <Modal
        open={!!(editItem ?? isCreate)}
        onCancel={onCloseModal}
        loading={isLoadingLeadType}
        okButtonProps={{
          htmlType: 'submit',
          loading: updateMutation.isPending || createMutation.isPending,
          onClick: onSubmit
        }}
      >
        <Form form={form} layout="vertical">
          <FormNomenclaturesLeadTypesStatuses />
        </Form>
      </Modal>

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCloseModal}
        confirmLoading={deleteLeadTypeMutation.isPending}
      />
    </>
  )
}
